import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, CardTitle, Text } from "@andrum/ui";
import {
  fetchDailyInsight,
  markInsightOpen,
  type DailyInsight,
} from "@/features/learn/api/insights-api";
import { FeedbackBar } from "@/features/learn/components/FeedbackBar";

type Props = {
  onOpenLearn?: () => void;
  onTryExperiment?: (slug: string | null) => void;
  tryBusy?: boolean;
};

function paragraphs(text: string): string[] {
  return text
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
}

/**
 * Daily insight on Today: title, plain body, try-today, one clear CTA.
 * No fake links, no “kort sagt” footer junk.
 */
export function DailyInsightCard({
  onOpenLearn,
  onTryExperiment,
  tryBusy = false,
}: Props) {
  const { t } = useTranslation();
  const [insight, setInsight] = useState<DailyInsight | null>(null);
  const [whyForYou, setWhyForYou] = useState<string | null>(null);
  const [personalized, setPersonalized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setFailed(false);
      const res = await fetchDailyInsight();
      if (cancelled) return;
      if (res?.insight) {
        setInsight({
          ...res.insight,
          whyForYou: res.insight.whyForYou ?? res.whyForYou ?? null,
        });
        setWhyForYou(res.whyForYou ?? res.insight.whyForYou ?? null);
        setPersonalized(Boolean(res.personalized));
        void markInsightOpen(res.insight.id, true);
      } else {
        setFailed(true);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Card className="space-y-1 border-border/70 px-4 py-3">
        <Text variant="caption">{t("dailyInsight.loading")}</Text>
      </Card>
    );
  }

  if (!insight) {
    return (
      <Card className="space-y-2 border-border/70 px-4 py-3">
        <Text variant="label">{t("dailyInsight.label")}</Text>
        <Text variant="subtle" className="text-sm leading-snug">
          {failed
            ? t("dailyInsight.unavailable")
            : t("dailyInsight.empty")}
        </Text>
      </Card>
    );
  }

  const linkedSlug = insight.suggestedExperimentSlug;
  const forYou = whyForYou ?? insight.whyForYou;
  const tryToday =
    insight.sections?.tryToday ?? insight.tryToday ?? insight.takeaway;
  // Prefer catalog idea only — skip generic “meaning” padding from the API when present
  const ideaSource = insight.sections?.idea ?? insight.body;
  const bodyParas = paragraphs(ideaSource);

  return (
    <Card className="space-y-3 border-border/70 px-4 py-3.5">
      <div className="flex items-center justify-between gap-2">
        <Text variant="label">{t("dailyInsight.label")}</Text>
        {personalized ? (
          <Text variant="caption" className="text-primary">
            {t("dailyInsight.forYouBadge")}
          </Text>
        ) : null}
      </div>

      <CardTitle as="h3" className="text-lg leading-snug tracking-tight">
        {insight.title}
      </CardTitle>

      {forYou ? (
        <div className="rounded-xl bg-primary-soft/40 px-3 py-2.5">
          <Text variant="caption" className="font-medium text-primary">
            {t("dailyInsight.whyForYou")}
          </Text>
          <Text
            variant="body"
            className="mt-1 text-sm leading-relaxed text-foreground"
          >
            {forYou}
          </Text>
        </div>
      ) : null}

      <div className="space-y-2.5">
        {bodyParas.map((para, i) => (
          <Text key={i} variant="body" className="text-sm leading-relaxed">
            {para}
          </Text>
        ))}
      </div>

      {tryToday ? (
        <div className="rounded-xl border border-border/80 bg-surface-muted/50 px-3 py-2.5">
          <Text variant="caption" className="font-medium">
            {t("dailyInsight.tryToday")}
          </Text>
          <Text variant="body" className="mt-1 text-sm leading-relaxed">
            {tryToday}
          </Text>
        </div>
      ) : null}

      <FeedbackBar source="insight" sourceId={insight.id} />

      <div className="flex flex-col gap-2 pt-0.5">
        {onTryExperiment ? (
          <Button
            fullWidth
            variant="soft"
            className="min-h-10"
            loading={tryBusy}
            onClick={() => onTryExperiment(linkedSlug)}
          >
            {linkedSlug
              ? t("dailyInsight.tryLinked")
              : t("dailyInsight.tryStep")}
          </Button>
        ) : null}
        {onOpenLearn ? (
          <button
            type="button"
            className="py-1 text-center text-xs font-medium text-foreground-muted transition-colors duration-200 hover:text-foreground"
            onClick={onOpenLearn}
          >
            {t("dailyInsight.openPast")}
          </button>
        ) : null}
      </div>
    </Card>
  );
}
